import React, { useEffect, useRef, useId, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  Loader2,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Undo2,
  X,
  Send,
  ChevronUp,
  ChevronDown,
  Code2,
} from 'lucide-react';
import { renderBlock } from './rendering/render-block';
import type {
  DashboardBlock,
  DashboardBlockSpec,
  RenderHandle,
  RenderConfig,
  Position,
} from '@/types/dashboard';

const SIZE_OPTIONS: { label: string; w: number; h: number }[] = [
  { label: '1x1', w: 1, h: 1 },
  { label: '2x1', w: 2, h: 1 },
  { label: '1x2', w: 1, h: 2 },
  { label: '2x2', w: 2, h: 2 },
];

interface DashboardBlockCardProps {
  block: DashboardBlock;
  data: Record<string, unknown>[];
  viewOnly?: boolean;
  previewMode?: boolean;
  onRenderHandle?: (handle: RenderHandle) => void;
  onDisposeHandle?: (blockId: string) => void;
  onKeep?: (blockId: string) => void;
  onDiscard?: (blockId: string) => void;
  onFeedback?: (blockId: string, blockTitle: string, feedback: string) => void;
  onUndoKeep?: (blockId: string) => void;
  onReorder?: (blockId: string, direction: 'up' | 'down') => void;
  onResize?: (blockId: string, position: Position) => void;
  onInspect?: (blockId: string) => void;
}

function toBlockSpec(block: DashboardBlock): DashboardBlockSpec {
  return {
    id: block.id,
    block_type: block.block_type,
    title: block.title,
    subtitle: block.subtitle,
    render_config: block.render_config,
    position: block.position,
    status: block.status,
  };
}

const DashboardBlockCard: React.FC<DashboardBlockCardProps> = ({
  block,
  data,
  viewOnly = false,
  previewMode = false,
  onRenderHandle,
  onDisposeHandle,
  onKeep,
  onDiscard,
  onFeedback,
  onUndoKeep,
  onReorder,
  onResize,
  onInspect,
}) => {
  const uniqueId = useId();
  const containerId = `block-${block.id}-${uniqueId.replace(/:/g, '')}`;
  const handleRef = useRef<RenderHandle | null>(null);
  const prevConfigRef = useRef<RenderConfig | null>(null);
  const prevDataRef = useRef<Record<string, unknown>[] | null>(null);
  const mountedRef = useRef(false);

  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isDiscarding, setIsDiscarding] = useState(false);
  const feedbackInputRef = useRef<HTMLTextAreaElement>(null);

  // Mount: render the chart
  useEffect(() => {
    const timer = requestAnimationFrame(() => {
      const container = document.getElementById(containerId);
      if (!container || !data || data.length === 0) return;

      try {
        const spec = toBlockSpec(block);
        const handle = renderBlock(containerId, spec, data);
        handleRef.current = handle;
        prevConfigRef.current = block.render_config;
        prevDataRef.current = data;
        mountedRef.current = true;
        onRenderHandle?.(handle);
      } catch (err) {
        console.error(`Failed to render block ${block.id}:`, err);
      }
    });

    return () => {
      cancelAnimationFrame(timer);
      if (handleRef.current) {
        handleRef.current.dispose();
        onDisposeHandle?.(block.id);
        handleRef.current = null;
        mountedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, containerId]);

  // Update: handle data or config changes, including late-arriving data
  useEffect(() => {
    if (!data || data.length === 0) return;

    if (!mountedRef.current) {
      const timer = requestAnimationFrame(() => {
        const container = document.getElementById(containerId);
        if (!container || handleRef.current) return;

        try {
          const spec = toBlockSpec(block);
          const handle = renderBlock(containerId, spec, data);
          handleRef.current = handle;
          prevConfigRef.current = block.render_config;
          prevDataRef.current = data;
          mountedRef.current = true;
          onRenderHandle?.(handle);
        } catch (err) {
          console.error(`Failed to render block ${block.id}:`, err);
        }
      });
      return () => cancelAnimationFrame(timer);
    }

    if (!handleRef.current) return;

    const configChanged =
      prevConfigRef.current !== null &&
      JSON.stringify(block.render_config) !==
        JSON.stringify(prevConfigRef.current);

    const dataChanged =
      prevDataRef.current !== null &&
      JSON.stringify(data) !== JSON.stringify(prevDataRef.current);

    if (configChanged) {
      handleRef.current.rerender(block.render_config);
      prevConfigRef.current = block.render_config;
      if (data && data.length > 0) {
        handleRef.current.update(data);
        prevDataRef.current = data;
      }
    } else if (dataChanged && data && data.length > 0) {
      handleRef.current.update(data);
      prevDataRef.current = data;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.render_config, data]);

  // Auto-focus feedback input when opened
  useEffect(() => {
    if (showFeedbackInput && feedbackInputRef.current) {
      feedbackInputRef.current.focus();
    }
  }, [showFeedbackInput]);

  const handleDiscard = useCallback(() => {
    setIsDiscarding(true);
    setTimeout(() => {
      onDiscard?.(block.id);
    }, 300);
  }, [block.id, onDiscard]);

  const handleFeedbackSubmit = useCallback(() => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    onFeedback?.(block.id, block.title, trimmed);
    setFeedbackText('');
    setShowFeedbackInput(false);
  }, [block.id, block.title, feedbackText, onFeedback]);

  const handleFeedbackKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleFeedbackSubmit();
      } else if (e.key === 'Escape') {
        setShowFeedbackInput(false);
        setFeedbackText('');
      }
    },
    [handleFeedbackSubmit]
  );

  const borderColor = viewOnly
    ? 'border-l-gray-300 dark:border-l-gray-600'
    : block.status === 'accepted'
      ? 'border-l-success'
      : block.status === 'rejected'
        ? 'border-l-destructive'
        : 'border-l-gray-400 dark:border-l-gray-600';

  const isKpi = block.block_type === 'kpi-card';
  const minHeight = isKpi ? 'min-h-[120px]' : 'min-h-[300px]';
  const hasData = data && data.length > 0;

  return (
    <div
      className={clsx(
        'rounded-xl border border-border',
        'bg-card',
        'border-l-4',
        borderColor,
        'overflow-hidden transition-all duration-300',
        isDiscarding && 'opacity-0 scale-95'
      )}
    >
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-medium text-foreground truncate">
            {block.title}
          </h3>
          {block.subtitle && (
            <p className="text-xs text-muted-foreground truncate">
              {block.subtitle}
            </p>
          )}
        </div>

        {/* Action area — depends on status and mode */}
        {viewOnly ? (
          <button
            onClick={() => onInspect?.(block.id)}
            title="Inspect query & data"
            className="p-1.5 rounded-md text-muted-foreground hover:text-info hover:bg-info/15 transition-colors flex-shrink-0"
          >
            <Code2 className="w-3.5 h-3.5" />
          </button>
        ) : previewMode ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => onInspect?.(block.id)}
              title="Inspect query & data"
              className="p-1.5 rounded-md text-muted-foreground hover:text-info hover:bg-info/15 transition-colors"
            >
              <Code2 className="w-3.5 h-3.5" />
            </button>
            <div className="flex items-center gap-0.5 p-0.5 rounded-md bg-accent">
              {SIZE_OPTIONS.map((opt) => {
                const isActive =
                  block.position.w === opt.w && block.position.h === opt.h;
                return (
                  <button
                    key={opt.label}
                    onClick={() =>
                      onResize?.(block.id, {
                        ...block.position,
                        w: opt.w,
                        h: opt.h,
                      })
                    }
                    title={`Resize to ${opt.label}`}
                    className={clsx(
                      'px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors',
                      isActive
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col">
              <button
                onClick={() => onReorder?.(block.id, 'up')}
                title="Move up"
                className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronUp className="w-3 h-3" />
              </button>
              <button
                onClick={() => onReorder?.(block.id, 'down')}
                title="Move down"
                className="p-0.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
        ) : (
          <>
            {block.status === 'draft' && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => onInspect?.(block.id)}
                  title="Inspect query & data"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-info hover:bg-info/15 transition-colors"
                >
                  <Code2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onKeep?.(block.id)}
                  title="Keep this block"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-success hover:bg-success/15 transition-colors"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleDiscard}
                  title="Discard this block"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/15 transition-colors"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setShowFeedbackInput((v) => !v)}
                  title="Give feedback on this block"
                  className={clsx(
                    'p-1.5 rounded-md transition-colors',
                    showFeedbackInput
                      ? 'text-primary bg-accent'
                      : 'text-muted-foreground hover:text-primary hover:bg-accent'
                  )}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {block.status === 'accepted' && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => onInspect?.(block.id)}
                  title="Inspect query & data"
                  className="p-1.5 rounded-md text-muted-foreground hover:text-info hover:bg-info/15 transition-colors"
                >
                  <Code2 className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-success/15 text-success">
                  Kept
                </span>
                <button
                  onClick={() => onUndoKeep?.(block.id)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Undo2 className="w-3 h-3" />
                  Undo
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Inline feedback input */}
      {!viewOnly && !previewMode && showFeedbackInput && block.status === 'draft' && (
        <div className="px-4 py-2.5 border-b border-border bg-muted/50">
          <div className="flex items-start gap-2">
            <textarea
              ref={feedbackInputRef}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              onKeyDown={handleFeedbackKeyDown}
              placeholder={`Feedback on "${block.title}"...`}
              rows={2}
              className="flex-1 text-sm bg-card border border-border rounded-md px-3 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-ring dark:focus:ring-ring text-foreground placeholder:text-muted-foreground"
            />
            <div className="flex flex-col gap-1">
              <button
                onClick={handleFeedbackSubmit}
                disabled={!feedbackText.trim()}
                className="p-1.5 rounded-md text-primary hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => {
                  setShowFeedbackInput(false);
                  setFeedbackText('');
                }}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chart container */}
      <div className={clsx('relative', minHeight)}>
        {!hasData && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-xs">Loading data...</span>
            </div>
          </div>
        )}
        <div
          id={containerId}
          className={clsx('w-full h-full', minHeight)}
          style={{ visibility: hasData ? 'visible' : 'hidden' }}
        />
      </div>
    </div>
  );
};

export default DashboardBlockCard;
